import { useMemo, useState } from 'react'
import { Box, Button, Flex, Icon, Modal, Text, Tooltip } from 'ui'
import { useEditMemberContext } from '../context/EditMemberProvider'
import { useAuthContext } from 'components/auth'
import { InfoIcon } from 'lucide-react'
import { AllCompaniesMembersResponse } from 'api/company'
import { toTitleCase } from 'lib/string'
import { getAddRemoveDisabledTooltip } from '../addRemoveDisabled'

interface Props {
  company: AllCompaniesMembersResponse['members'][number]['companies'][number]
  label?: string
}

export const RemoveCompanyButton = ({ company, label = 'Remove' }: Props) => {
  const {
    state: { isPending, member, addRemoveDisabledReason },
    actions: { removeCompany },
  } = useEditMemberContext()

  const { company: currentCompany } = useAuthContext()
  const [isRemoveCompanyModalOpen, setIsRemoveCompanyModalOpen] =
    useState(false)

  if (addRemoveDisabledReason) {
    return (
      <Tooltip content={getAddRemoveDisabledTooltip(addRemoveDisabledReason)}>
        <Box display="inline-block">
          <Button variant="secondary" kind="small" danger disabled>
            {label}
          </Button>
        </Box>
      </Tooltip>
    )
  }

  const companyIdsUserHasAccessTo = useMemo(() => {
    if (!currentCompany) {
      return []
    }

    return currentCompany?.companyGroup?.companies?.length
      ? currentCompany.companyGroup.companies.map((c) => Number(c.id))
      : [Number(currentCompany.id)]
  }, [currentCompany?.companyGroup?.companies])

  const handleRemoveCompany = async () => {
    await removeCompany(company.id)
    setIsRemoveCompanyModalOpen(false)
  }

  if (!companyIdsUserHasAccessTo.includes(company.id)) {
    return (
      <Flex flexDirection="row" alignItems="flex-end" gap={2}>
        <Button variant="secondary" kind="small" disabled danger>
          {label}
        </Button>
        <Tooltip content="You do not have permission to remove user's access to this company">
          <Icon icon={InfoIcon} size={16} />
        </Tooltip>
      </Flex>
    )
  }

  const isMemberLastCompany = member?.companies.length === 1

  return (
    <Modal
      title="Remove company access"
      open={isRemoveCompanyModalOpen}
      onClose={() => setIsRemoveCompanyModalOpen(false)}
      trigger={
        <Button
          variant="secondary"
          kind="small"
          danger
          disabled={isPending}
          onClick={() => setIsRemoveCompanyModalOpen(true)}
        >
          {label}
        </Button>
      }
      showCancel
      onOk={handleRemoveCompany}
      okText="Remove"
      loading={isPending}
    >
      <Text>
        Remove {toTitleCase(company.name)} from{' '}
        {toTitleCase(member?.name || '')} access?
      </Text>
      {!!isMemberLastCompany && (
        <Text mt={2}>
          This action will remove {toTitleCase(member?.name)}'s access to
          WorkWhile's portal.
        </Text>
      )}
    </Modal>
  )
}
